import { useNavigate } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    BuildCircle as BuildCircleIcon,
    Circle as CircleIcon
} from "@mui/icons-material";
import { lightBlue, green, yellow } from "@mui/material/colors";

import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";
import { IStepperProps } from "../interfaces/componentInterfaces";
import { Typography } from "@initia-digital/component-library";

const styles = {
    ACTIVE: { color: lightBlue['800'] },
    COMPLETED: { color: green['500'] },
    PENDING: { color: yellow['600'] }
}


export const DefaultSimpleStepper = (props: IStepperProps) => {
    // -------------------------------------------
    // PROPS
    const { steps, activeStepValue } = props;

    // DERIVED PROPS
    let activeStepIndex = steps.findIndex(step => step.value === activeStepValue);
    if ( activeStepIndex === -1 ) { activeStepIndex = 0; }

    // NEXT HOOKS
    const navigate = useNavigate();
    const path = window.location.pathname;

    // MUI HOOKS
    const theme = useTheme();
    const screenMdOrUp = useMediaQuery(theme.breakpoints.up('md'));    



    return (
        <Box display="flex" flexDirection="column">
            <Stack spacing={1}>
                { 
                    steps.map(( step, idx ) => {
                        const { label, query } = step;
                        let supportingStyling: { color: string } = styles.PENDING;
                        if (activeStepIndex === idx) { supportingStyling = styles.ACTIVE; }
                        else if (activeStepIndex > idx) { supportingStyling = styles.COMPLETED; }
                        const icon = (activeStepIndex === idx) ? <BuildCircleIcon sx={{ fontSize: "1.2rem", ...supportingStyling }} /> : <CircleIcon sx={{ fontSize: "1rem", ...supportingStyling }} />
                        return (
                            <Box 
                                key={`default-simple-stepper-${idx}`}
                                display="flex" flexDirection="row" alignItems="center" 
                                py="5px" px="10px" borderRadius="10px"
                                sx={{ cursor: "pointer", "&:hover": { border: "1px solid lightgrey" } }}
                                onClick={() => {
                                    navigate(`${path}${query}`);
                                }}
                            >
                                <Box mr="5px" display="flex" justifyContent="flexStart" alignItems="center">
                                    { icon }
                                </Box>
                                <Box mr="5px" display="flex" justifyContent="flexStart" alignItems="center">
                                        <Typography
                                            color={theme.palette.text.primary}
                                            fontWeight={300}
                                            fontSize={screenMdOrUp ? '1rem' : '0.9rem'}
                                        >
                                        { label }
                                        </Typography>
                                </Box>                
                            </Box>    
                        )
                    })
                }   
            </Stack>
        </Box>

    )
}

export const MobileResponsiveSimpleStepper = (props: IStepperProps) => {
    // -------------------------------------------
    // PROPS
    const { steps, activeStepValue } = props;

    // DERVIED PROPS
    let activeStep = steps.find(step => step.value === activeStepValue);
    if (!baseDefinedAndNotNullNarrowing(activeStep)) { activeStep = steps[0]; }

    // CUSTOM HOOKS
    const theme = useTheme();
    const isSmallerThanLg = useMediaQuery(theme.breakpoints.down('lg'));

    if (!isSmallerThanLg) {
        return (
            <DefaultSimpleStepper {...props} />
        )
    }

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Typography>{activeStep!.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <DefaultSimpleStepper {...props} />
            </AccordionDetails>
        </Accordion>
    )
}